import {useEffect, useState} from 'react'
import {useLocation, useParams, useNavigate} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {useWindowSize} from 'react-use'
import moment from 'moment-timezone'
import {toast} from 'react-toastify'

import {
  useGetWalletCardTemplateListSelectQuery,
  useLazyGetWalletCardQuery,
  useLazyGetWalletCardTemplateDetailQuery,
} from '../../../../../services/cellcast.v2'
import {useGetUserProfileDetailsQuery} from '../../../../../services/cellcast'
import {getUserData} from '../../../../modules/auth/components/Frontend_Auth_Components/UserLoginRedux/UserLoginSlice'
import {useTimezone} from '../../../../hooks/useTimezone'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {AppResponse, PromoAction} from '../../../../../services/cellcast.v2.types'
import {
  CARD_TYPE,
  CardViewType,
  STEP_KEYS,
  DEFAULT_FORM_FIELDS,
  DEFAULT_STEP,
  steps,
} from '../../../../Utils/Constant/WalletCard'
import {FormSetting, CardData, processFormSettings, ShareMethod} from '../type'
import {startLoadding, stopLoading} from '../../../../Redux/FrontEnd/Loading/LoadingSlice'

interface UseWalletCardParams {
  isEdit: boolean
  isTemplate: boolean
  templateId: string | null
}

export const useWalletCard = ({isEdit, isTemplate, templateId}: UseWalletCardParams) => {
  const location = useLocation()
  const {cardId} = useParams<{cardId: string}>()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  /* ------------------------------------------------------------- */
  /*                       USER & DATE HELPERS                      */
  /* ------------------------------------------------------------- */
  const currentUser = useSelector(getUserData)
  const userId = currentUser?.user?._id || currentUser?._id
  const {data: userProfileResponse, isLoading: isLoadingUser} = useGetUserProfileDetailsQuery(
    userId || ''
  )
  const userData = userProfileResponse?.data?.user
  const {userTimezone} = useTimezone()
  const {t} = useI18n()

  const getDefaultName = () => {
    const date = new Date()
    const format = 'YYMMDD'
    return moment(date)
      .tz(userTimezone || moment.tz.guess())
      .format(format)
  }

  /* ------------------------------------------------------------- */
  /*                       RESPONSIVE HELPERS                      */
  /* ------------------------------------------------------------- */
  const {width} = useWindowSize()
  const isSmallScreen = width < 1420

  /* ------------------------------------------------------------- */
  /*                          API HOOKS                            */
  /* ------------------------------------------------------------- */
  const {data: templateListResp, refetch} = useGetWalletCardTemplateListSelectQuery(undefined, {
    skip: isTemplate,
  })

  useEffect(() => {
    if (!isTemplate) {
      refetch()
    }
  }, [isTemplate, refetch])

  const templateOptions = templateListResp?.data || []

  const [getWalletCard, {isLoading}] = useLazyGetWalletCardQuery()
  const [getWalletCardTemplateDetail, {isLoading: isLoadingTemplate}] =
    useLazyGetWalletCardTemplateDetailQuery()

  /* ------------------------------------------------------------- */
  /*                            STATES                             */
  /* ------------------------------------------------------------- */
  const [walletCardTemplateID, setWalletCardTemplateID] = useState<string>('')
  const [promoSettingIds, setPromoSettingIds] = useState<string[]>([])
  const [vehicleTypeOptionIds, setVehicleTypeOptionIds] = useState<string[]>([])
  const [promoActionLabels, setPromoActionLabels] = useState<string[]>([])
  const [currentView, setCurrentView] = useState<CardViewType>(CardViewType.GOOGLE)

  const [expandedStep, setExpandedStep] = useState<string>(DEFAULT_STEP)

  const [activeSteps, setActiveSteps] = useState<Record<string, boolean>>({
    gettingStarted: true,
    cardDetails: false,
    shareContactDetails: false,
  })

  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({
    gettingStarted: false,
    cardDetails: false,
    shareContactDetails: false,
    formConnect: false,
  })

  // Track whether a step has ever been opened/expanded in this session
  const [visitedSteps, setVisitedSteps] = useState<Record<string, boolean>>({
    gettingStarted: true, // default expanded
    cardDetails: false,
    shareContactDetails: false,
    formConnect: false,
  })

  const cardName = isTemplate ? '' : `${userData?.name} Card ${getDefaultName()}`
  const [cardData, setCardData] = useState<CardData>({
    user: userId || '',
    type: CARD_TYPE[0].key || 'businessCard',
    organizationName: '',
    cardName: isEdit ? '' : cardName,
    cardLabel: '',
    fullName: isEdit ? '' : userData?.name,
    jobTitle: '',
    companyName: '',
    address: '',
    phone: isEdit ? '' : userData?.mobile,
    email: isEdit ? '' : userData?.email,
    website: '',
    shareMethod: [ShareMethod.WALLET],
    logo: '',
    picture: '',
    pictureGoogle: '',
    backgroundImage: '',
    backgroundColor: '#ffffff',
    foregroundColor: '#000000',
    labelColor: '#000000',
    promoSettings: [],
    formSettings: DEFAULT_FORM_FIELDS,
    formBackgroundColor: '#ffffff',
    formLabelColor: '#000000',
    contactIconColor: '#8734cb',
    template: '',
    isDefault: false,
    status: 'draft',
    vehicleTypeOptions: [],
  })

  const [formFields, setFormFields] = useState<FormSetting[]>(cardData.formSettings)

  /* ------------------------------------------------------------- */
  /*                        SIDE EFFECTS                           */
  /* ------------------------------------------------------------- */

  // Set default template selection
  useEffect(() => {
    if (templateOptions.length > 0 && !isEdit && !isTemplate) {
      const templateID = templateId ? templateId : templateOptions[0].id
      setWalletCardTemplateID(templateID)
    }
  }, [templateOptions, isEdit, isTemplate])

  // Sync userId and user data into cardData when available
  useEffect(() => {
    if (userId && userData) {
      setCardData((prev) => ({
        ...prev,
        user: userId,
        fullName: prev.fullName || userData.name,
        phone: prev.phone || userData.mobile,
        email: prev.email || userData.email,
      }))
    }
  }, [userId, userData])

  // Fetch card data when editing an existing card or template
  useEffect(() => {
    if (isEdit && cardId) {
      const fetchFn = isTemplate ? getWalletCardTemplateDetail : getWalletCard

      const params = {id: cardId}
      if (!isTemplate) dispatch(startLoadding())

      fetchFn(params)
        .unwrap()
        .then((response: AppResponse<unknown>) => {
          const data = response?.data
          if (!data || typeof data !== 'object') return

          const card = data as Partial<CardData>
          const cardWithDefaults: CardData = {
            ...cardData,
            ...(card as CardData),
            contactIconColor: card.contactIconColor || cardData.contactIconColor || '#8734cb',
          }

          if (!isTemplate) {
            setWalletCardTemplateID(card.walletCardTemplate || '')
          }

          setCardData(cardWithDefaults)

          const promoSettingsValue = (card as {promoSettings?: unknown}).promoSettings
          setPromoSettingIds(
            Array.isArray(promoSettingsValue)
              ? (promoSettingsValue as PromoAction[]).map((p) => p.id)
              : []
          )

          const vehicleTypeValue = (card as {vehicleTypeOptions?: unknown}).vehicleTypeOptions
          const normalizedVehicleIds = Array.isArray(vehicleTypeValue)
            ? (vehicleTypeValue as Array<{_id?: string; id?: string} | string>).map((v) =>
                typeof v === 'string' ? v : (v?._id ?? (v as {id?: string}).id ?? '')
              )
            : []
          setVehicleTypeOptionIds(normalizedVehicleIds)

          const formSettingsValue = (card as {formSettings?: unknown}).formSettings
          setFormFields(
            processFormSettings(Array.isArray(formSettingsValue) ? (formSettingsValue as FormSetting[]) : [])
          )
        })
        .catch(() => {
          toast.error(t('WALLET_CARD.ERROR'))
          navigate('/wallet-card')
        })
        .finally(() => {
          dispatch(stopLoading())
        })
    }
  }, [isEdit, cardId, isTemplate])

  // Fetch template detail when changing template selection (create mode)
  useEffect(() => {
    if (!isTemplate && walletCardTemplateID) {
      getWalletCardTemplateDetail({id: walletCardTemplateID})
        .unwrap()
        .then((response: AppResponse<unknown>) => {
          const data = response?.data
          if (!data || typeof data !== 'object') return

          const card = data as Partial<CardData>
          setCardData((prev) => {
            const preservedName = prev.cardName || card.cardName
            const preservedStatus = prev.status || card.status
            const preservedJobTitle = prev.jobTitle || card.jobTitle
            const preservedPhone = prev.phone || card.phone
            const preservedEmail = prev.email || card.email
            const preservedFullName = prev.fullName || card.fullName
            const preservedPictureGoogle = prev.pictureGoogle || card.pictureGoogle
            const preservedPicture = prev.picture || card.picture

            const merged: CardData = {
              ...prev,
              ...(card as Partial<CardData>),
              id: cardId,
              cardName: preservedName ?? prev.cardName,
              status: preservedStatus ?? prev.status ?? 'draft',
              jobTitle: preservedJobTitle ?? prev.jobTitle,
              phone: preservedPhone ?? prev.phone,
              email: preservedEmail ?? prev.email,
              fullName: preservedFullName ?? prev.fullName,
              pictureGoogle: preservedPictureGoogle ?? prev.pictureGoogle,
              picture: preservedPicture ?? prev.picture,
            }

            return merged
          })
          const promoSettingsValue = (card as {promoSettings?: unknown}).promoSettings
          setPromoSettingIds(
            Array.isArray(promoSettingsValue)
              ? (promoSettingsValue as PromoAction[]).map((p) => p.id)
              : []
          )

          const vehicleTypeValue = (card as {vehicleTypeOptions?: unknown}).vehicleTypeOptions
          const normalizedVehicleIds = Array.isArray(vehicleTypeValue)
            ? (vehicleTypeValue as Array<{_id?: string; id?: string} | string>).map((v) =>
                typeof v === 'string' ? v : (v?._id ?? (v as {id?: string}).id ?? '')
              )
            : []
          setVehicleTypeOptionIds(normalizedVehicleIds)

          const formSettingsValue = (card as {formSettings?: unknown}).formSettings
          setFormFields(
            processFormSettings(Array.isArray(formSettingsValue) ? (formSettingsValue as FormSetting[]) : [])
          )
        })
        .catch(() => {
          toast.error(t('WALLET_CARD.ERROR'))
          navigate('/wallet-card')
        })
        .finally(() => {
          dispatch(stopLoading())
        })
    }
  }, [walletCardTemplateID, isTemplate])

  // Handle location state data (copy/create from another page)
  useEffect(() => {
    const state = location.state as {data?: CardData}
    if (state?.data) {
      const cardWithDefaults = {
        ...state.data,
        contactIconColor: state.data.contactIconColor || '#8734cb',
      }
      setCardData(cardWithDefaults)
      setPromoSettingIds(state.data?.promoSettings?.map((p: PromoAction) => p.id) || [])
      const vt = state.data?.vehicleTypeOptions
      setVehicleTypeOptionIds(
        Array.isArray(vt)
          ? (vt as Array<{_id?: string; id?: string} | string>).map((v) =>
              typeof v === 'string' ? v : (v?._id ?? (v as {id?: string}).id ?? '')
            )
          : []
      )
      setFormFields(processFormSettings(state.data?.formSettings || []))
    }
  }, [location.state])

  // Sync active steps when expandedStep changes
  useEffect(() => {
    if (expandedStep) {
      const newActive: Record<string, boolean> = {}
      steps.forEach((s) => {
        newActive[s.key] = s.key === expandedStep
      })
      setActiveSteps(newActive)
      // Mark the step as visited when it becomes the expanded step (e.g., via preview icon)
      setVisitedSteps((prev) => ({...prev, [expandedStep]: true}))
    }
  }, [expandedStep])

  /* ------------------------------------------------------------- */
  /*                          HANDLERS                             */
  /* ------------------------------------------------------------- */
  const handleCardDataChange = (data: Partial<CardData>) => {
    setCardData((prev) => ({...prev, ...data}))
  }

  const handleCardTypeChange = (type: string) => {
    setCardData((prev) => ({...prev, type}))
  }

  const handlePromoActionList = (labels: string[]) => {
    setPromoActionLabels(labels)
  }

  const handleStepClick = (stepIndex: number) => {
    const stepKey = steps[stepIndex].key
    if (!stepKey) return

    // Update active steps
    const newActive: Record<string, boolean> = {}
    steps.forEach((s) => {
      newActive[s.key] = s.key === stepKey
    })
    setActiveSteps(newActive)

    // Update expanded step
    setExpandedStep(stepKey)

    // Mark step as visited
    setVisitedSteps((prev) => ({...prev, [stepKey]: true}))

    // Sync preview view with step
    if (stepKey === STEP_KEYS.FORM_CONNECT) {
      setCurrentView(CardViewType.FORM_SETTING)
    } else if (currentView === CardViewType.FORM_SETTING) {
      setCurrentView(CardViewType.CONTACT)
    }
  }

  const handleStepExpand = (stepKey: string) => {
    const idx = steps.findIndex((s) => s.key === stepKey)
    if (idx !== -1) handleStepClick(idx)
  }

  /* ------------------------------------------------------------- */
  /*                           RETURN                              */
  /* ------------------------------------------------------------- */
  // Gate completion ticks for template creation: steps 3 and 4 must be opened at least once
  const displayCompletedSteps: Record<string, boolean> = (() => {
    if (isTemplate && !isEdit) {
      return {
        ...completedSteps,
        [STEP_KEYS.SHARE_CONTACT_DETAILS]:
          !!completedSteps[STEP_KEYS.SHARE_CONTACT_DETAILS] &&
          !!visitedSteps[STEP_KEYS.SHARE_CONTACT_DETAILS],
        [STEP_KEYS.FORM_CONNECT]:
          !!completedSteps[STEP_KEYS.FORM_CONNECT] && !!visitedSteps[STEP_KEYS.FORM_CONNECT],
      }
    }
    return completedSteps
  })()

  return {
    // data
    cardData,
    formFields,
    promoSettingIds,
    vehicleTypeOptionIds,
    promoActionLabels,
    templateOptions,
    walletCardTemplateID,
    isLoading: isLoading || isLoadingTemplate || isLoadingUser,
    // states
    isSmallScreen,
    activeSteps,
    completedSteps: displayCompletedSteps,
    visitedSteps,
    currentView,
    expandedStep,
    // setters
    setFormFields,
    setPromoSettingIds,
    setVehicleTypeOptionIds,
    setWalletCardTemplateID,
    setCompletedSteps,
    setCurrentView,
    setExpandedStep,
    // handlers
    handleCardDataChange,
    handleCardTypeChange,
    handlePromoActionList,
    handleStepClick,
    handleStepExpand,
  }
}
